import React, { useState, useEffect } from 'react';
import { createSession } from '../utils/apiService';
import CardManagement from './CardManagement';
import chevronUp from '../images/chevron-up.png';

const USER_TYPES = [
  {
    value: 'budget',
    label: 'Budget Traveler',
    description: 'Focus: Lowest cost, deals, discounts'
  },
  {
    value: 'luxury',
    label: 'Luxury Traveler',
    description: 'Focus: Premium experiences, comfort, exclusivity'
  },
  {
    value: 'family',
    label: 'Family Traveler',
    description: 'Focus: Safety, convenience for kids, family-friendly activities'
  },
  {
    value: 'adventure',
    label: 'Adventure Seeker',
    description: 'Focus: Outdoor activities, thrill experiences'
  },
  {
    value: 'cultural',
    label: 'Cultural Explorer',
    description: 'Focus: Local traditions, history, authentic experiences'
  },
  {
    value: 'business',
    label: 'Business Traveler',
    description: 'Focus: Efficiency, connectivity, comfort during work trips'
  },
  {
    value: 'solo',
    label: 'Solo Traveler',
    description: 'Focus: Independence, safety, social opportunities'
  }
];



function SettingsPanel({
  isOpen,
  onClose,
  sessionId,
  setSessionId,
  userId,
  setUserId,
  onTestPaymentModal,
  onTestCredentialResponse,
  userInfo,
  updateUserInfo,
  updateSessionId,

}) {
  const [formData, setFormData] = useState({
    userType: ''
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Initialize form data from userInfo when panel opens or userInfo changes
  useEffect(() => {
    if (userInfo) {
      setFormData({
        userType: userInfo.userType || ''
      });
    }
  }, [userInfo, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
 
  const handleSave = async () => {
    const newErrors = {};

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
     
      // If userType changed, recreate session
      if (formData.userType && userInfo?.name) {
        try {
          const sessionResponse = await createSession(userInfo.name, formData.userType);
          updateSessionId(sessionResponse.session_id);
          setUserId(sessionResponse.user_id || userInfo.name);
        } catch (error) {
          console.error('Failed to update session:', error);
        }
      }

      // Show success message or close panel
      showNotification('Settings saved successfully', 'success');
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      showNotification('Failed to save settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCardSelected = (selectedCard) => {
    console.log('Card selected in SettingsPanel:', selectedCard);
    
    // Update user info with selected card
    const updatedUserInfo = {
      ...userInfo,
      cardInfo: {
        cardNumber: selectedCard.cardNumber,
        cardHolder: selectedCard.cardHolder,
        expiryDate: selectedCard.expiryDate,
        cvv: selectedCard.cvv
      }
    };
    
    updateUserInfo(updatedUserInfo);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };


  const handleTestPaymentAndClose = () => {
    onTestPaymentModal();
    onClose();
  };

  const handleTestCredentialAndClose = () => {
    onTestCredentialResponse();
    onClose();
  };


  return (
    <div className={`settings-panel ${isOpen ? 'open' : ''}`}>
      <div className="settings-header">
        <h2>Settings</h2>
        <button className="close-btn" onClick={onClose}>
          ×
        </button>
      </div>

      
      <div className="settings-content">

          {/* Card Management Section */}
        <CardManagement 
          sessionId={sessionId}
          key={isOpen ? 'open' : 'closed'}
          userId={userId}
          onCardSelected={handleCardSelected}
          showdiscount={false}
          showDelete={true}
        />

        {/* User Type Field */}
        <div className="form-group">
          <label htmlFor="userType">User Type</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <select
                id="userType"
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                className={errors.userType ? 'error' : ''}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  paddingRight: '40px',
                  width: '100%'
                }}
              >
                <option value="">Select your travel preference</option>
                {USER_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <img 
                src={chevronUp} 
                alt="dropdown" 
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%) rotate(180deg)',
                  width: '16px',
                  height: '16px',
                  pointerEvents: 'none'
                }}
              />
            </div>
            <button
              type="button"
              className="settings-btn save-btn"
              onClick={handleSave}
              disabled={isSaving}
              style={{ whiteSpace: 'nowrap' }}
            >
              {isSaving ? 'Updating...' : 'Update User'}
            </button>
          </div>
          {errors.userType && <span className="error-message">{errors.userType}</span>}
          {formData.userType && (
            <div className="user-type-description">
              {USER_TYPES.find(t => t.value === formData.userType)?.description}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="sessionId">Session ID</label>
          <input
            type="text"
            id="sessionId"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            placeholder="Auto-generated if empty"
          />
        </div>
        <div className="form-group">
          <label htmlFor="userId">User ID</label>
          <input
            type="text"
            id="userId"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="Anonymous if empty"
          />
        </div>
        <div className="form-group"  style={{ display:'none'}}>
          <label>Testing Tools</label>
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            <button
              className="settings-btn"
              onClick={handleTestPaymentAndClose}
              style={{ width: '100%', margin: 0 }}
            >
              🧪 Test Payment Modal
            </button>
          
          </div>
        </div>
      </div>

      {/* Notification Popup */}
      {notification.show && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '16px 24px',
          backgroundColor: notification.type === 'success' ? '#4CAF50' : '#f44336',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 10000,
          fontSize: '14px',
          fontWeight: '500',
          minWidth: '250px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          transition: 'opacity 0.3s ease-out',
          opacity: 1
        }}>
          <span style={{ fontSize: '20px' }}>
            {notification.type === 'success' ? '✓' : '✕'}
          </span>
          {notification.message}
        </div>
      )}
    </div>
  );
}

export default SettingsPanel;
